/**
 * Operational Summary dashboard types — issue #819, Phase 2.
 *
 * The payload returned by GET /dashboard/operational/summary. Wraps
 * data produced by an `OperationalSignalAdapter` that the platform
 * uses to talk to whichever observability backend the project is
 * configured for (Datadog, Dynatrace, Grafana Cloud, Prometheus, New
 * Relic, or the built-in MockOperationalAdapter used when no real
 * backend is configured).
 *
 * Grounded in Google SRE's Four Golden Signals (Latency / Traffic /
 * Errors / Saturation), the RED method for request-path services,
 * the USE method for saturation, and DORA's four software-delivery
 * metrics. Cross-cuts the structural graph: every signal attaches to
 * an entity (service, user experience, flow) the structural analyzer
 * already knows about.
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────
// Golden signals per service (one row per backend endpoint or
// service grouping).
// ─────────────────────────────────────────────────────────────────
export const GoldenSignalsSchema = z.object({
    /** Stable identifier — maps to a BCG RouteEndpoint.routeId or module. */
    serviceId: z.string(),
    serviceName: z.string(),
    /** Optional framework for display chip. */
    framework: z.string().optional(),
    /** Latency quantiles in milliseconds. */
    latency: z.object({
        p50: z.number().nonnegative(),
        p95: z.number().nonnegative(),
        p99: z.number().nonnegative(),
    }),
    /** Requests per second averaged over the window. */
    rate: z.number().nonnegative(),
    /** Error fraction 0..1. */
    errorRate: z.number().min(0).max(1),
    /** Saturation 0..1 (e.g. CPU load, queue depth). */
    saturation: z.number().min(0).max(1),
});
// ─────────────────────────────────────────────────────────────────
// SLO status & error budget burn.
// ─────────────────────────────────────────────────────────────────
export const SloBurnBandSchema = z.enum([
    'healthy',
    'slow', // < 2x
    'fast', // 2x–10x
    'critical', // > 10x
]);
export const SLOStatusSchema = z.object({
    sloId: z.string(),
    name: z.string(),
    /** Target achievement (e.g. 0.99 for 99%). */
    target: z.number().min(0).max(1),
    /** Window string as shown to users, e.g. "30d" / "7d". */
    window: z.string(),
    /** Current achievement 0..1 over the window. */
    currentAchievement: z.number().min(0).max(1),
    /** Burn rate multiplier. 1 = at budget; 10 = would burn budget 10x faster. */
    burnRate: z.number().nonnegative(),
    burnBand: SloBurnBandSchema,
    /** User experiences this SLO protects (derived from graph traversal). */
    userExperienceIds: z.array(z.string()).default([]),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// Synthetic monitoring results per user experience.
// ─────────────────────────────────────────────────────────────────
export const SyntheticStatusSchema = z.enum(['pass', 'fail', 'degraded']);
export const SyntheticResultSchema = z.object({
    userExperienceId: z.string(),
    status: SyntheticStatusSchema,
    /** Pass rate 0..1 over the window. */
    passRate: z.number().min(0).max(1),
    /** ISO timestamp of the last probe. */
    lastRunAt: z.string(),
    /** Plain-text failure reason when status !== 'pass'. */
    lastFailReason: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────
// DORA metrics.
// ─────────────────────────────────────────────────────────────────
export const DoraTierSchema = z.enum(['elite', 'high', 'medium', 'low']);
export const DoraMetricsSchema = z.object({
    /** Deploys per week averaged over the window. */
    deploymentFrequencyPerWeek: z.number().nonnegative(),
    /** Lead time from commit → prod, in hours. */
    leadTimeHours: z.number().nonnegative(),
    /** Fraction of deploys that required immediate remediation. */
    changeFailureRate: z.number().min(0).max(1),
    /** Mean time to restore, in hours. */
    mttrHours: z.number().nonnegative(),
    /** Classification per DORA's Elite/High/Medium/Low rubric. */
    tier: DoraTierSchema,
});
// ─────────────────────────────────────────────────────────────────
// Incidents.
// ─────────────────────────────────────────────────────────────────
export const IncidentSeveritySchema = z.enum(['critical', 'high', 'medium', 'low']);
export const IncidentSchema = z.object({
    id: z.string(),
    title: z.string(),
    severity: IncidentSeveritySchema,
    /** ISO timestamps. resolvedAt === null means still active. */
    startedAt: z.string(),
    resolvedAt: z.string().nullable(),
    /** Graph-derived: which user experiences this incident touched. */
    affectedUserExperienceIds: z.array(z.string()).default([]),
    projectId: z.string(),
    projectName: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// User-experience health matrix — the signature panel section.
// One row per detected user interaction / experience.
// ─────────────────────────────────────────────────────────────────
export const UserExperienceHealthRowSchema = z.object({
    userExperienceId: z.string(),
    name: z.string(),
    projectId: z.string(),
    projectName: z.string(),
    syntheticStatus: SyntheticStatusSchema,
    syntheticPassRate: z.number().min(0).max(1),
    /** RED rollup across every endpoint in the flow. */
    red: z.object({
        rate: z.number().nonnegative(),
        errorRate: z.number().min(0).max(1),
        /** p95 duration in ms. */
        durationP95Ms: z.number().nonnegative(),
    }),
    /** Worst SLO burn band across the SLOs this flow depends on. */
    sloBurnBand: SloBurnBandSchema,
    /** How many distinct endpoints this flow traverses. */
    endpointsTouched: z.number().int().nonnegative(),
});
// ─────────────────────────────────────────────────────────────────
// Per-project adapter configuration status.
// ─────────────────────────────────────────────────────────────────
export const OperationalAdapterKindSchema = z.enum([
    'mock',
    'datadog',
    'dynatrace',
    'grafana',
    'prometheus',
    'newrelic',
    'none',
]);
export const ProjectAdapterStatusSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    adapter: OperationalAdapterKindSchema,
    /** True when the adapter is reachable / healthy. */
    reachable: z.boolean(),
});
// ─────────────────────────────────────────────────────────────────
// Top-level payload.
// ─────────────────────────────────────────────────────────────────
export const OperationalTilesSchema = z.object({
    /** 0..1 — fraction of user experiences where the latest synthetic run passed. */
    syntheticPassRate24h: z.number().min(0).max(1),
    /** Count of incidents with resolvedAt === null. */
    activeIncidents: z.number().int().nonnegative(),
    /** SLOs burning at > 10x rate. */
    sloBurnCritical: z.number().int().nonnegative(),
    deploymentFrequencyPerWeek: z.number().nonnegative(),
    changeFailureRate: z.number().min(0).max(1),
    mttrHours: z.number().nonnegative(),
});
export const OperationalSummaryPayloadSchema = z.object({
    schemaVersion: z.literal(1),
    generatedAt: z.string(),
    projectId: z.string().nullable(),
    projectCount: z.number().int().nonnegative(),
    /** Count of projects where a real (non-mock) adapter is configured. */
    adaptersConfigured: z.number().int().nonnegative(),
    /** Projects using the mock adapter because nothing else is configured. */
    adaptersMocked: z.number().int().nonnegative(),
    tiles: OperationalTilesSchema,
    userExperienceMatrix: z.array(UserExperienceHealthRowSchema),
    /** True when the matrix was capped for payload-size reasons. */
    userExperienceMatrixTruncated: z.boolean().default(false),
    /** Total row count before truncation; lets the UI show "showing X of Y". */
    userExperienceMatrixTotal: z.number().int().nonnegative().default(0),
    goldenSignals: z.array(GoldenSignalsSchema),
    sloList: z.array(SLOStatusSchema),
    incidents: z.array(IncidentSchema),
    /** DORA across the entire scope (aggregated; not per-project). */
    dora: DoraMetricsSchema,
    /** Per-project adapter configuration so the UI can prompt to connect. */
    adapterStatus: z.array(ProjectAdapterStatusSchema),
});
//# sourceMappingURL=operationalSummary.js.map